import { usePostShapefileGeojsonConversioni } from '@framework/api-client/api/geocoding/geo-json-to-shapefile-controller/geo-json-to-shapefile-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { DraggerInput } from '@framework/form/inputs/upload/DraggerInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { message } from 'antd';
import { type RcFile } from 'antd/es/upload';
import { type Feature, type FeatureCollection, type Polygon } from 'geojson';
import * as React from 'react';
import { useWatch } from 'react-hook-form';
import { type Subject } from 'rxjs';
import { type MapSubjectEvent } from '../../MapWidgetComponent.tsx';
import { geoJSONEpsg3857 } from '../../mapStyleUtils.ts';
import { MapUploadGeometriesType } from './MapUploadGeometriesSchema.tsx';

export interface IMapUploadGeometriesShapefileInputProps {
  mapSubject: React.RefObject<Subject<MapSubjectEvent>>;
  onClose: () => void;
  onLoadingChange: (loading: boolean) => void;
}

function isGeoJSONFeatureCollection(data: any): data is FeatureCollection {
  return (
    data != null &&
    typeof data === 'object' &&
    data.type === 'FeatureCollection' &&
    Array.isArray(data.features)
  );
}

export function MapUploadGeometriesShapefileInput(
  props: IMapUploadGeometriesShapefileInputProps,
) {
  const { mapSubject, onClose, onLoadingChange } = props;

  const selectedType: MapUploadGeometriesType | null = useWatch({
    name: 'type',
  });

  const convertShapefileToGeoJSON = usePostShapefileGeojsonConversioni();
  const isUploadingAt = React.useRef(Date.now());

  const handleUpload = async (allFiles: RcFile[]) => {
    if (Date.now() - isUploadingAt.current < 100) return;
    isUploadingAt.current = Date.now();

    const files = allFiles.filter(isShapefileExtension);
    const missingExtensions = missingShapefileExtensions(files);
    if (missingExtensions) {
      // biome-ignore format: <tbd>
      message.warning(`Non sono stati inclusi tutti i file necessari per lo Shapefile, mancano i seguenti: ${missingExtensions.join(', ')}`);
      return;
    }

    onLoadingChange(true);
    try {
      const data = await convertShapefileToGeoJSON.mutateAsync({
        data: {
          file: files.map(file => file as Blob),
        },
      });

      if (data.avviso?.messaggio != null) {
        // biome-ignore format: <tbd>
        message.error(`Non è stato possibile importare il file (MWSE002): ${data.avviso.messaggio}`);
        console.error("Errore durante l'importazione dello shapefile", data);
        return;
      }

      if (!isGeoJSONFeatureCollection(data?.geojson)) {
        // biome-ignore format: <tbd>
        message.error('Errore durante la conversione dello shapefile in GeoJSON: non è stato possibile ottenere le geometrie. (MWSE001)');
        // biome-ignore format: <tbd>
        console.error('Errore durante la conversione dello shapefile in GeoJSON', data);
        return;
      }

      let conversionWarnings = 0;
      const convertedFeatures = data.geojson.features
        .map(feature => {
          if (feature.geometry.type === 'Polygon') {
            return feature;
          }

          // Le API restituiscono MultiPolygon come Polygon, ma con un solo poligono
          // (Coordinate[0]). Convertiamo quindi MultiPolygon in Polygon lato client.
          if (feature.geometry.type === 'MultiPolygon') {
            if (feature.geometry.coordinates.length !== 1) {
              console.warn('MultiPolygon con più di un poligono', feature);
              conversionWarnings++;
              return null;
            }

            return {
              ...feature,
              geometry: {
                ...feature.geometry,
                type: 'Polygon',
                coordinates: feature.geometry.coordinates[0],
              },
            };
          }
        })
        .filter((f): f is Feature<Polygon> => f != null)
        .map(feature => geoJSONEpsg3857.readFeature(feature));

      if (conversionWarnings > 0) {
        // biome-ignore format: <tbd>
        message.warning(`${conversionWarnings} geometrie del file non sono state riconosciute. Assicurarsi che siano tutti poligoni.`);
      }

      mapSubject.current?.next({
        type: 'upload',
        features: convertedFeatures,
      });
      onClose();
    } catch (e) {
      // biome-ignore format: <tbd>
      message.error(getNetworkErrorMessage(e, 'Errore durante la conversione dello shapefile. (MWSE005)'));
      // biome-ignore format: <tbd>
      console.error('Errore durante la conversione dello shapefile in GeoJSON', e);
    } finally {
      onLoadingChange(false);
    }
  };

  if (selectedType !== MapUploadGeometriesType.Shapefile) return null;

  return (
    <FormItem name="files" label="File">
      <DraggerInput
        name="files"
        accept={'.shp,.shx,.dbf,.prj'}
        multiple
        info="Selezionare i file che compongono lo Shapefile (.shp, .shx, .dbf, .prj)"
        showUploadList={false}
        beforeUpload={async (file, fileList) => {
          await handleUpload(fileList);
          return false;
        }}
      />
    </FormItem>
  );
}

const shapefileExtensions = ['.shp', '.shx', '.dbf', '.prj'];

function isShapefileExtension(file: RcFile) {
  return shapefileExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
}

function missingShapefileExtensions(files: RcFile[]) {
  const missingExtensions = shapefileExtensions.filter(
    ext => !files.some(f => f.name.toLowerCase().endsWith(ext)),
  );

  if (missingExtensions.length === 0) return null;
  return missingExtensions;
}
